import { ArrowUpRight } from "lucide-react";
import Link from "next/link";

// Interface for blog data (plain object from JSON)
interface BlogCardProps {
  blog: {
    slug: string;
    title: string;
    description: string;
    thumbnail?: string;
    category?: string;
    tags?: string[];
    publishedAt?: string;
  };
}

export default function BlogCard({ blog }: BlogCardProps) {
  const handleCardClick = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Helper functions (since we're using plain objects)
  const getDescriptionPreview = (
    description: string,
    maxLength: number = 60
  ) => {
    if (!description) return "";
    return description.length > maxLength
      ? description.substring(0, maxLength) + "..."
      : description;
  };

  const getBlogUrl = (slug: string) => `/blog/${slug}`;

  // Fallback image if no thumbnail
  const defaultImage =
    "https://res.cloudinary.com/ddf2lw8vs/image/upload/v1749759115/ChatGPT_Image_Jun_13_2025_01_41_10_AM_zfruuc.png";

  // Create a title for the image alt text
  const imageAltText = `${blog.title} - wedding blog post`;

  return (
    <Link href={getBlogUrl(blog.slug)} passHref>
      <div className="flex flex-col cursor-pointer">
        {/* Image container with background color */}
        <div className="relative bg-[#FFFFFF] rounded-lg overflow-hidden border border-[#F2F2F2] shadow-[0px_0px_11.6px_-1px_rgba(186,186,186,0.25)]">
          <div className="w-full p-6">
            <img
              src={blog.thumbnail || defaultImage}
              alt={imageAltText}
              className="w-full h-auto rounded-lg"
              loading="lazy"
            />
            <div className="pt-2 pb-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-base font-semibold text-[#2C2C2C] line-clamp-2">
                  {blog.title}
                </h3>
                <ArrowUpRight
                  size={16}
                  className="text-[#2C2C2C] flex-shrink-0 ml-2"
                />
              </div>

              <p className="text-[12px] text-[#6B6B6B] leading-[1.4]">
                {getDescriptionPreview(blog.description, 80)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

// Alternative version with more props for flexibility
interface BlogCardFlexibleProps {
  blog?: any; // Optional blog object
  title: string;
  description: string;
  slug: string;
  thumbnail?: string;
  category?: string;
  tags?: string[];
  publishedAt?: string;
  onClick?: () => void;
}

export function BlogCardFlexible({
  blog,
  title,
  description,
  slug,
  thumbnail,
  category,
  tags,
  publishedAt,
  onClick,
}: BlogCardFlexibleProps) {
  const handleCardClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const getDescriptionPreview = (text: string, maxLength: number = 80) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const getBlogUrl = (blogSlug: string) => `/blog/${blogSlug}`;
  const defaultImage =
    "https://res.cloudinary.com/ddf2lw8vs/image/upload/v1749759115/ChatGPT_Image_Jun_13_2025_01_41_10_AM_zfruuc.png";
  const imageAltText = `${title} - wedding blog post`;

  return (
    <Link href={getBlogUrl(slug)} passHref>
      <div className="flex flex-col cursor-pointer" onClick={handleCardClick}>
        <div className="relative bg-[#FFFFFF] rounded-lg overflow-hidden border border-[#F2F2F2] shadow-[0px_0px_11.6px_-1px_rgba(186,186,186,0.25)]">
          <div className="w-full p-6">
            <img
              src={thumbnail || defaultImage}
              alt={imageAltText}
              className="w-full h-auto rounded-lg"
              loading="lazy"
            />
            <div className="pt-2 pb-4">
              {category && (
                <p className="text-[10px] text-[#EB4A4A] font-medium mb-1">
                  {category}
                </p>
              )}

              <div className="flex items-center justify-between mb-1">
                <h3 className="text-base font-semibold text-[#2C2C2C] line-clamp-2">
                  {title}
                </h3>
                <ArrowUpRight
                  size={16}
                  className="text-[#2C2C2C] flex-shrink-0 ml-2"
                />
              </div>

              <p className="text-[12px] text-[#6B6B6B] leading-[1.4]">
                {getDescriptionPreview(description)}
              </p>

              {tags && tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {tags.slice(0, 2).map((tag, index) => (
                    <span
                      key={index}
                      className="text-[9px] bg-[#F5F5F5] text-[#6B6B6B] px-2 py-1 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

// Usage in your BlogContent component:
//
// {blogs.map((blog) => (
//   <BlogCard
//     key={blog.slug}
//     blog={blog}
//   />
// ))}
//
// Or with flexible props:
//
// {blogs.map((blog) => (
//   <BlogCardFlexible
//     key={blog.slug}
//     title={blog.title}
//     description={blog.description}
//     slug={blog.slug}
//     thumbnail={blog.thumbnail}
//     category={blog.category}
//     tags={blog.tags}
//   />
// ))}
